import { Box, Container, Grid, Theme, useMediaQuery } from '@mui/material';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { Card, Form, MarbleButton } from 'src/base';
import withMarketingLayout from 'src/components/layout/MarketingLayout';
import { MarbleText } from 'src/base/texts/Text';
import { useApiStore, useForm } from 'ui-sdk/src/hooks';
import { streetAddress } from 'server-sdk/src/types';
import { useRouter } from 'ui-sdk/src/router';
import { MarbleIcon } from 'src/base/outputs/icons';
import { validateObject, validateString } from 'ui-sdk/src/util/validate';
import { track } from 'src/deps/mixpanel';
import { usd } from 'server-sdk/src/util';

const containerStyle: React.CSSProperties = {
  backgroundColor: 'white',
  paddingTop: 40,
  maxWidth: 1000,
  marginBottom: 80,
};

// rent estimate page
function InvestHome(): JSX.Element {
  const router = useRouter();
  const [investData, refreshInvestData] = useApiStore((a) => a.invest.data);
  const [joined, joinEmailList] = useApiStore((a) => a.invest.joinEmailList);
  // const handlePropertyClick = (e) => {
  //   const link = `/rent-estimate/${encodeURIComponent(
  //     e.addr.replace(/[, ]+/g, '-').toLowerCase(),
  //   )}?beds=${e.beds}&baths=${e.baths}`;
  //   window.open(link, '_blank');
  // };
  useEffect(() => {
    refreshInvestData();
  }, []);

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const form = useForm({
    initial: {
      email: '',
    },
    validationSchema: validateObject({
      email: validateString().email(),
    }),
    submit: (values) => {
      joinEmailList(
        {
          email: values.email,
        },
        () => {
          track('Invest Page Email Track', {
            email: values.email,
          });
          form.resetForm({
            email: '',
          });
        },
      );
    },
  });
  return (
    <>
      <Head>
        <title>Direct Offers - Marble Virtual Rental Management</title>
        <meta
          name="description"
          content="Send and receive direct offers on investment properties on Marble."
        />
      </Head>
      <Container style={containerStyle}>
        <MarbleText size={isMobile ? 'xl' : 'h2'} weight="bold">
          Direct Offers Catalogue
        </MarbleText>
        <MarbleText
          weight="med"
          sx={{
            pt: 1,
            pb: 3,
          }}
        >
          Purchase rental properties direct from other qualified investors
          verified by the Marble Acquisition Team.
        </MarbleText>
        {/* <MarbleButton
          title="List Your Property"
          href="/invest/add"
          sx={{
            mt: 3,
            mb: 5,
          }}
        /> */}
        <MarbleText weight="med">
          Get monthly emails with new opportunities
        </MarbleText>
        <Box
          sx={(t) => ({
            pb: 3,
            [t.breakpoints.up('sm')]: {
              width: '50%',
            },
          })}
        >
          <Form
            form={form}
            steps={[
              {
                name: 'email',
                placeholder: 'Email',
                type: 'email',
                required: true,
              },
            ]}
          />
          <MarbleButton
            title={joined ? 'Joined' : 'Sign Up'}
            variant="glass"
            disabled={Boolean(joined)}
            color={joined ? 'gray' : 'blue'}
            onClick={() => {
              form.onSubmit();
            }}
          />
        </Box>
        {investData?.sections?.map(({ areaTitle, listings }) => (
          <Box pt={5}>
            <Box>
              <MarbleText size="xl" weight="med">
                {areaTitle}
              </MarbleText>
            </Box>
            <Box pt={3}>
              <Grid container spacing={1} display="flex" wrap="wrap">
                {listings?.map(
                  ({
                    addr_slug,
                    avgRent,
                    address: { addr1, addr2, city, state },
                    thumbnailSrc,
                    saleEstimate,
                  }) => (
                    <Grid item xs={12} sm={4} m={0} p={0}>
                      <Card
                        onClick={() => {
                          router.push(
                            {
                              pathname: `/invest/${addr_slug}`,
                            },
                            undefined,
                            {
                              shallow: true,
                            },
                          );
                        }}
                        style={{
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                          }}
                        >
                          <img
                            src={thumbnailSrc}
                            style={{
                              width: '100%',
                              // maxWidth: 300,
                              height: 165,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                            alt={`rental property at ${streetAddress(
                              addr1,
                              addr2,
                            )}`}
                          />
                        </Box>
                        <Grid container direction="column">
                          <Grid item>
                            <Box>
                              <MarbleText weight="bold">
                                {usd(saleEstimate, 0)}
                              </MarbleText>
                              <MarbleText size="s">
                                {streetAddress(addr1, addr2)}
                              </MarbleText>
                              <MarbleText size="s" color="gray.1">
                                {city}, {state}
                              </MarbleText>
                            </Box>
                          </Grid>
                          <Grid item pt={2} pb={1} pr={2}>
                            <Grid
                              container
                              justifyContent="space-between"
                              alignItems="flex-end"
                              p={0}
                            >
                              <Grid item p={0}>
                                <MarbleText size="s" weight="bold">
                                  ${avgRent}
                                </MarbleText>
                                <MarbleText color="gray.1" size="s">
                                  Estimated Income
                                </MarbleText>
                              </Grid>
                              <Grid item p={0}>
                                <MarbleIcon name="chevron-right" />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  ),
                )}
              </Grid>
            </Box>
          </Box>
        ))}
      </Container>
    </>
  );
}

export default withMarketingLayout(InvestHome);
